import moment from "moment";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import LoadingScreen from "../components/common/LoadingScreen";
import { fetchAuth } from "../reducers/dashboard/DashboardActions";
import AuthHelper from "../utils/AuthHelper";
import commerceAPI from "../utils/CommerceAPI";
import Constants from "../utils/Constants";
import { sendLogin as sendLoginTag } from "../utils/TagManagerHelper";
import RouteConstants from "../utils/RouteConstants";
import AWSAPI from "../utils/AWSAPI";
import {
  msalInstance,
  b2cPolicies,
  apiScope,
  ensureMsalInitialized,
  logout,
} from "../config/AuthProvider";

export const MsalContext = React.createContext();
export const useMsalContext = () => useContext(MsalContext);

function AzureWrapperContent({ children }) {
  const { REACT_APP_MAINTENANCE_MODE } = process.env;
  const urlParams = new URLSearchParams(window.location.search);
  const isMaintenance =
    REACT_APP_MAINTENANCE_MODE === "true" &&
    !urlParams.has(process.env.REACT_APP_MAINTENANCE_MODE_BACKDOOR);

  const [isLoading, setIsLoading] = useState(true);
  const [isMsalReady, setIsMsalReady] = useState(false);
  const [isRedirectHandling, setIsRedirectHandling] = useState(false);
  const [isLoginRedirectInProgress, setIsLoginRedirectInProgress] = useState(false);
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const REFRESH_THRESHOLD_MINUTES = 0;
  const dispatch = useDispatch();

  // Wait for MSAL to be ready - use centralized initialization
  useEffect(() => {
    const checkMsalReady = async () => {
      try {
        await ensureMsalInitialized();
        setIsMsalReady(true);
      } catch (error) {
        // Continue anyway to prevent blocking the app
        setIsMsalReady(true);
      }
    };

    checkMsalReady();
  }, []);

  const isPathnamePublic = (pathname) => {
    const enabledEndpoints = [
      "/forgot-password",
      "/change-password",
      "/signup-confirmation",
      "/country-not-available",
      "/invitation-error",
      "/service-maintenance",
      "/membership",
      "/payment-successful",
      "/logout",
      `(${RouteConstants.PRODUCT_PAGE_URL})([A-Za-z0-9])*`,
      `(${RouteConstants.INSIGHTS_PAGE_URL})([A-Za-z0-9])*`,
      `(${RouteConstants.HIRE_A_TEAM_PAGE_URL})([A-Za-z0-9])*`,
      `(${RouteConstants.VIDEO_LIBRARY_URL})([A-Za-z0-9])*`,
      `(${RouteConstants.AREA42LABS_PAGE_URL})([A-Za-z0-9])*`,
      "/accessibility-statement",
    ];

    return enabledEndpoints.some((enabledPath) => {
      const regex = new RegExp(enabledPath);
      return regex.test(pathname) || pathname === "/";
    });
  };

  const setLoadingStateFinished = (forceRedirect = false) => {
    if (forceRedirect) {
      AuthHelper.redirectToSavedLocation(setIsLoading);
    } else if (!isLoginRedirectInProgress) {
      // Only stop loading if we're not in the middle of a login redirect
      setIsLoading(false);
    }
  };

  const executeNewLogin = async () => {
    sendLoginTag();

    let response;
    let authorityToUse = b2cPolicies.authorities.signIn.authority; // Declare outside try-catch

    try {
      // Get the B2C authority from the account
      const account = accounts[0];
      // const accountAuthority = account?.idTokenClaims?.iss || account?.environment;
      const policyUsed = account?.idTokenClaims?.tfp;

      // Determine which B2C policy authority to use
      if (policyUsed) {
        // Map the policy to the correct authority
        if (policyUsed.includes("signup") || policyUsed.includes("SignUp")) {
          authorityToUse = b2cPolicies.authorities.signUp.authority;
        } else if (policyUsed.includes("passwordreset") || policyUsed.includes("PasswordReset")) {
          authorityToUse = b2cPolicies.authorities.resetPassword.authority;
        } else if (policyUsed.includes("forgotpassword") || policyUsed.includes("ForgotPassword")) {
          authorityToUse = b2cPolicies.authorities.forgotPassword.authority;
        }
      }

      response = await instance.acquireTokenSilent({
        ...apiScope,
        account: accounts[0],
        authority: authorityToUse,
      });
    } catch (error) {
      // Fallback with acquireTokenRedirect for interaction required errors
      if (error instanceof InteractionRequiredAuthError || error.errorCode === "AADB2C90027") {
        await instance.acquireTokenRedirect({
          ...apiScope,
          account: accounts[0],
          authority: authorityToUse, // Now accessible in catch block
        });
        return;
      }

      commerceAPI.defaultErrorHandler(error);
      return;
    }

    // Always execute commerceAPI logic if we have a response
    if (!sessionStorage.getItem(Constants.SW_CONTEXT_TOKEN)) {
      if (!sessionStorage.getItem(Constants.IS_LOGGED_IN_USER_DASHBOARD_ENABLED)) {
        dispatch(fetchAuth());
      }

      commerceAPI
        .login(response.accessToken)
        .then((apiResponse) => {
          sessionStorage.setItem(
            Constants.SW_CONTEXT_TOKEN,
            apiResponse.data[Constants.SW_CONTEXT_TOKEN],
          );
          sessionStorage.setItem(Constants.IS_LOGGED_IN_USER_REPLYER, apiResponse.data.isReplyer);

          const reqBody = { email: AuthHelper.getEmail() };

          AWSAPI.isQualifiedCredentials(reqBody).then((resAPI) => {
            sessionStorage.setItem(
              Constants.IS_LOGGED_IN_USER_QUALIFIED_INNOVATION_TOUR,
              resAPI.data.isQualifiedIT,
            );
          });

          setLoadingStateFinished(true);
        })
        .catch((e) => {
          commerceAPI.defaultErrorHandler(e);
          const status = e && e.response && e.response.status;
          if (status === 412) {
            setLoadingStateFinished(false);
          }
        });
    } else {
      setLoadingStateFinished(true);
    }

    // Add auth cookie for dam/authorizer
    const { accessToken } = response;
    let cleanAccessToken = accessToken;
    if (typeof cleanAccessToken === "string") {
      cleanAccessToken = cleanAccessToken.replace(/^"(.*)"$/, "$1");
    }
    const expirationDate = new Date(response.expiresOn);
    document.cookie = `roseAuth=${accessToken}; path=/; domain=reply.com; Secure; SameSite=None; expires=${expirationDate.toUTCString()};`;
  };

  const isPathnameBreakingEarly = (pathname) => {
    const pathnames = [
      "/signup-confirmation",
      "/country-not-available",
      "/invitation-error",
      "/service-maintenance",
      "/forgot-password",
      "/change-password",
      "/membership",
      "/payment-successful",
    ];

    return pathnames.includes(pathname);
  };

  const requestNewToken = async () => {
    if (accounts[0]?.idTokenClaims?.exp) {
      if (
        moment
          .unix(accounts[0].idTokenClaims.exp)
          .isBefore(moment().add(REFRESH_THRESHOLD_MINUTES, "minutes"))
      ) {
        try {
          await instance.acquireTokenSilent({
            ...apiScope,
            account: accounts[0],
          });
        } catch (error) {
          // If silent token acquisition fails, sign out
          logout();
          return;
        }
      }

      // Execute login process
      await executeNewLogin();
    }
  };

  useEffect(() => {
    if (!isMsalReady) return; // Wait for MSAL to be ready
    if (isRedirectHandling) return; // Don't interfere with redirect handling

    const k = async () => {
      if (!isMaintenance) {
        if (urlParams.has("redirect") && urlParams.get("redirect") === "home") {
          window.history.pushState({}, document.title, "/");
          AuthHelper.clearSavedRedirectLocation();
        }

        // special case for pathnames that respond to loggin in errors
        // and the change password flow
        const { pathname } = window.location;
        if (isPathnamePublic(pathname) && isPathnameBreakingEarly(pathname)) {
          setLoadingStateFinished(false);
          if (pathname === "/change-password") {
            // setBlockNotifications(false);
          }
          return undefined; // Explicitly return undefined
        }

        if (isAuthenticated && accounts.length > 0) {
          const account = accounts[0];
          if (!AuthHelper.isRegisteredFromReply(account.idTokenClaims?.email || "")) {
            AuthHelper.isCountryAllowed(account.idTokenClaims?.countryCode);
          }

          if (
            account.idTokenClaims?.tfp === Constants.TFP_FORGOT_PASSWORD_FLOW ||
            account.idTokenClaims?.tfp === Constants.TFP_RESET_PASSWORD_FLOW
          ) {
            setLoadingStateFinished(true);
            return undefined; // Explicitly return undefined
          }

          await requestNewToken();
        } else if (isPathnamePublic(window.location.pathname)) {
          setLoadingStateFinished(false);
        } else {
          try {
            setIsRedirectHandling(true);
            setIsLoginRedirectInProgress(true);

            // Keep loading state active during redirect
            instance.loginRedirect({
              scopes: ["openid", "profile"],
              authority: b2cPolicies.authorities.signIn.authority,
            });

            // The page will redirect, so we don't need to handle the loading state here
            // The loading will continue until the redirect completes and the user comes back
          } catch (error) {
            setIsRedirectHandling(false);
            setIsLoginRedirectInProgress(false);
            setLoadingStateFinished(false);
          }
        }
      } else {
        setLoadingStateFinished();
      }
      return undefined; // Explicitly return undefined at the end
    };

    k().catch((e) => {
      // eslint-disable-next-line no-console
      console.error("Error in AzureWrapper useEffect:", e);
      setIsRedirectHandling(false);
      setIsLoginRedirectInProgress(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accounts, isMaintenance, isMsalReady, isRedirectHandling]);

  useEffect(() => {
    if (!isMsalReady) return undefined; // Wait for MSAL to be ready

    const checkTokenExpiration = async () => {
      try {
        // Check if token exists and has expiration
        if (accounts[0]?.idTokenClaims?.exp) {
          const expirationMoment = moment.unix(accounts[0].idTokenClaims.exp);
          const currentMoment = moment();
          const minutesUntilExpiration = expirationMoment.diff(currentMoment, "minutes");
          if (minutesUntilExpiration <= REFRESH_THRESHOLD_MINUTES) {
            await requestNewToken();
          }
        }
      } catch (error) {
        await requestNewToken();
      }
    };

    // Initial check
    checkTokenExpiration();

    // Set up interval to check every 3 minutes
    const intervalId = setInterval(checkTokenExpiration, 3 * 60 * 1000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, isMsalReady]);

  // Determine if we should show loading
  const shouldShowLoading = isLoading || isLoginRedirectInProgress;

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <MsalContext.Provider value={{ isLoading: shouldShowLoading }}>
      <RenderChildren isLoading={shouldShowLoading}>{children}</RenderChildren>
    </MsalContext.Provider>
  );
}

AzureWrapperContent.propTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

AzureWrapperContent.defaultProps = {
  children: () => null,
};

export function AzureWrapper({ children }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AzureWrapperContent>{children}</AzureWrapperContent>
    </MsalProvider>
  );
}

AzureWrapper.propTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

AzureWrapper.defaultProps = {
  children: () => null,
};

function RenderChildren({ children, isLoading }) {
  if (isLoading) {
    return <LoadingScreen />;
  }
  return <> {children} </>;
}

RenderChildren.propTypes = {
  children: PropTypes.node.isRequired,
  isLoading: PropTypes.bool.isRequired,
};
